/**
 * StageLines — one SVG line per stage across a contiguous day axis, with a
 * legend that toggles each line and a crosshair that reads every visible line
 * for one day at a time.
 *
 * Both stage charts on the Search Progress tab are this component: "New
 * Candidates In Each Stage" plots the per-bucket counts, "Total In Each Stage"
 * plots the running sum of the same numbers. The difference is entirely in the
 * series handed in — see `stageProgress.ts`.
 *
 * SOME SERIES OPEN DEACTIVATED. Everything off the eight-rung recruiting ladder
 * starts hidden (`seriesDefaults.ts`), so the legend still lists every stage the
 * search has while the plot starts on the pipeline. That is also what keeps the
 * rescale below honest on real data: Rejected at 483 against Reference's 3 on
 * search 3378 flattens every other line on a shared axis.
 *
 * THE TOOLTIP IS A CROSSHAIR OVER THE WHOLE DAY, NOT A BUBBLE PER LINE, and the
 * reason is the actual question a reader brings: "how many Identified against
 * Pursuing on the 26th" is ONE question. A per-line tooltip makes them hover
 * three times and hold two numbers in their head while they do it. Hidden series
 * stay out of it — a toggle that hid a line from the chart but not from the
 * tooltip would be a toggle that half worked.
 *
 * ZERO IS PRINTED AS ZERO. The zero-fill in `stageProgressFrom` exists so a
 * quiet stretch reads as quiet rather than as missing; a tooltip that skipped
 * the zero rows would undo it at exactly the moment the reader went looking for
 * the gap. Every visible series appears on every day, 0 included.
 *
 * THE TOTALS LINE THE LEGACY DRAWS IS DELIBERATELY ABSENT. `ScoreboardService`
 * sums stage transitions and outreaches into a single "Totals" series, which
 * adds two different units: a stage transition is a candidacy moving, an
 * outreach is a message sent, and the sum is a number with no referent. Outreach
 * volume keeps its own chart and its own axis on this tab. A stages-only total
 * would be legitimate (FR6 allows it) and is still not drawn, because with the
 * legend already carrying eleven per-stage totals a twelfth line is ink rather
 * than information.
 *
 * The y-axis rescales to the VISIBLE series. That is what a per-line toggle is
 * for: on a real search Identified peaks at 708 against Reference's 3, and
 * hiding the big lines has to actually make the small ones readable or the
 * toggle is decoration.
 *
 * The SVG is decorative (`aria-hidden`); the legend is the accessible
 * representation and is also where each series' total lives, since the axis is
 * rescaled. The per-day figures exist nowhere else, so the crosshair is reachable
 * from the keyboard and feeds a live region — see `useNearestPoint`.
 * Design-system token utilities only; no inline `style`; English copy.
 */

import { useMemo } from "react";

import { stageFillClass, stageSeriesStyle } from "../../../../components/stages/chartPalette";
import { ChartTooltip, type ChartTooltipRow } from "./ChartTooltip";
import { SeriesLegend, allHiddenMessage, useSeriesToggles } from "./SeriesLegend";
import { SeriesSwatch } from "./SeriesSwatch";
import { dayLabel, labelStride, monthLabel, monthTicks } from "./dayAxis";
import { stageHiddenByDefault } from "./seriesDefaults";
import type { StageSeries } from "./stageProgress";
import { FALLBACK_CHART_WIDTH, useElementWidth } from "./useElementWidth";
import { type PlotGeometry, nearestSeriesKey, useNearestPoint } from "./useNearestPoint";

export interface StageLinesProps {
  /** Contiguous `YYYY-MM-DD`, ascending — the x-axis. */
  days: readonly string[];
  /** One entry per stage; `counts` must be aligned with `days`. */
  series: readonly StageSeries[];
  /** Shown when there is nothing to plot at all. */
  emptyMessage: string;
  /** Distinguishes the two stage charts in the DOM and in tests. */
  testId: string;
  /**
   * What this chart plots, for the focusable surface's accessible name. A
   * separate prop rather than a branch on `testId`, which is a test hook and
   * would make the announced name a thing a rename could silently change.
   */
  label: string;
  /**
   * How the tooltip names a point. Defaults to the full day; the Search Progress
   * tab passes a bucket-aware one so a week reads as "Week of Jul 20, 2026"
   * rather than as a bare Monday the reader has to recognise.
   */
  pointLabel?: (day: string) => string;
  /** What one x-axis step IS, for the summary line: `day`, `week`, `month`. */
  pointNoun?: string;
  /**
   * False renders a picture instead of an instrument: no focusable surface, no
   * crosshair, no tooltip, no live region, and a legend of plain text. For the
   * downloaded report, which runs no JavaScript — every one of those affordances
   * would be inert markup inviting an interaction that cannot happen.
   *
   * The per-point figures are the thing a static chart genuinely loses. The
   * legend still carries each series' window total, and the document's tables
   * carry the rest.
   */
  interactive?: boolean;
  /**
   * Overrides the measured width.
   *
   * `useElementWidth` measures in an effect, and `renderToStaticMarkup` runs no
   * effects — so a static render would fall back to `FALLBACK_CHART_WIDTH` and then
   * scale by `containerWidth / 480` in whatever browser opened the file,
   * re-creating the very font-scaling bug the measured width exists to fix. The
   * export passes the width its layout actually uses.
   */
  width?: number;
  /**
   * False drops the "N of M stages shown over K weeks, peak P" line under the
   * chart.
   *
   * Jared, 2026-08-25: *"You can get rid of also these bottom notes in each
   * chart."* Scoped to a prop rather than deleted outright because the downloaded
   * report has no legend to hover and no axis label to read a peak off — a
   * document keeps the sentence a screen can afford to lose.
   *
   * The "every stage is hidden" branch is NOT suppressed by this. That sentence
   * is not a footnote, it is the only thing standing between the reader and a
   * chart that looks broken — and since 2026-08-27 a chart CAN open with
   * everything hidden, on a search whose only stages are off the ladder.
   */
  showSummary?: boolean;
}

const HEIGHT = 200;
const PAD_LEFT = 8;
const PAD_RIGHT = 8;
const PAD_TOP = 18;
const PAD_BOTTOM = 26;
const PLOT_HEIGHT = HEIGHT - PAD_TOP - PAD_BOTTOM;

export function StageLines({
  days,
  series,
  emptyMessage,
  testId,
  label,
  pointLabel = dayLabel,
  pointNoun = "day",
  interactive = true,
  width: widthOverride,
  showSummary = true,
}: StageLinesProps) {
  // Off-ladder stages start deactivated — the rule, not a snapshot, so a stage
  // that only shows up once the reader widens the range arrives hidden too. See
  // `seriesDefaults.ts` for why it is derived from the ladder rather than listed.
  //
  // ONLY WHEN THE CHART IS AN INSTRUMENT. A `interactive={false}` render is a
  // picture in a downloaded document: the legend is plain text, there is no
  // toggle, and a hidden line is therefore indistinguishable from a line that
  // does not exist — with no way back. "Off by default" is a first view a reader
  // can change in one click; without the click it is data withheld from a
  // record. The document already ignored whatever the reader had hidden on
  // screen (it re-renders with fresh toggle state), so drawing everything there
  // is the behaviour this preserves rather than a new exception.
  const { hidden, toggle } = useSeriesToggles(
    series.map((s) => s.stage),
    interactive ? stageHiddenByDefault : undefined
  );
  const surface = useElementWidth(FALLBACK_CHART_WIDTH);
  // `widthOverride` wins over the measurement: a static render has no layout to
  // measure and `renderToStaticMarkup` runs no effects, so the export passes the
  // width its own document uses. Live pages pass nothing and measure.
  const width = widthOverride ?? surface.width;
  // Memoized on the measured width: `useNearestPoint` keys its callbacks on this
  // object's identity, so rebuilding it every render would defeat the memo and
  // re-attach the pointer handlers on every mouse move.
  const geometry = useMemo<PlotGeometry>(
    () => ({ width, padLeft: PAD_LEFT, padRight: PAD_RIGHT, height: HEIGHT }),
    [width]
  );
  const crosshair = useNearestPoint(days.length, geometry);
  const plotWidth = width - PAD_LEFT - PAD_RIGHT;

  // A LOOKUP, NOT AN ASSIGNMENT. `stageSeriesStyle` is a function of the stage
  // name alone, so hiding a line cannot recolour the lines that stay, widening
  // the date range cannot recolour a stage that was already drawn, and this
  // chart cannot disagree with the donut and the funnel on the other tab. The
  // map is built once per series list only to keep the render loop out of a
  // per-point function call.
  const styles = useMemo(
    () => new Map(series.map((s) => [s.stage, stageSeriesStyle(s.stage)] as const)),
    [series]
  );

  if (days.length === 0 || series.length === 0) {
    return (
      <p data-testid={`${testId}-empty`} className="text-body text-text-secondary">
        {emptyMessage}
      </p>
    );
  }

  const visible = series.filter((s) => !hidden.has(s.stage));
  const peak = Math.max(1, ...visible.flatMap((s) => s.counts));
  const step = days.length > 1 ? plotWidth / (days.length - 1) : 0;
  const x = (index: number) => PAD_LEFT + index * step + (days.length > 1 ? 0 : plotWidth / 2);
  const y = (count: number) => PAD_TOP + PLOT_HEIGHT - (count / peak) * PLOT_HEIGHT;

  // One label per month boundary rather than every Nth day: a 366-day window
  // labelled by index lands on arbitrary dates, and "Mar" under the March
  // section is what a reader is looking for.
  const ticks = monthTicks(days);
  // Derived from the width now that a viewBox unit is a pixel: a month label is
  // ~28px, so a 1200px chart has room for far more than 12 and being sparse
  // there was an artefact of the fixed 480-unit box, not a design choice.
  const labelEvery = labelStride(ticks.length, plotWidth);

  const focused = interactive ? crosshair.index : null;
  // WHICH LINE THE READER IS LOOKING AT. The bubble stays a whole-point
  // crosshair listing every visible series — that is one read of one question —
  // and this picks the series whose plotted point sits closest to the pointer,
  // so the row describing the line under the cursor is the one emphasised.
  //
  // Nearest is nearest: there is deliberately no distance cap. A cap would leave
  // a reader hovering slightly off every line with an un-highlighted tooltip and
  // no way to tell why. Null on a keyboard focus, because there is no pointer.
  // The nearest VISIBLE series to the pointer, so the row describing the line
  // under the cursor is the one emphasised. Shared with the other line chart —
  // this was nine duplicated lines carrying two `as number` casts each, because
  // the null check above them did not narrow into the closure.
  // `focused === null` short-circuits BEFORE the call rather than being encoded
  // as an empty array argument. Same result, but `focused` is then narrowed to a
  // number inside the closure — so the invariant is held by the type rather than
  // by two files agreeing to pass `[]` and read `focused ?? 0` (PR #752, N3).
  const highlightKey =
    focused === null
      ? undefined
      : nearestSeriesKey(
          visible,
          crosshair.pointerY,
          (s) => s.stage,
          (s) => y(s.counts[focused] ?? 0)
        );

  const tooltipRows: ChartTooltipRow[] =
    focused === null
      ? []
      : visible.map((s) => ({
          key: s.stage,
          label: s.stage,
          // `?? 0` covers a series shorter than the axis, which the fold should
          // never produce — but a blank cell would be indistinguishable from a
          // real zero, and those two mean opposite things here.
          value: String(s.counts[focused] ?? 0),
          swatch: (
            <SeriesSwatch
              strokeClass={styles.get(s.stage)?.strokeClass ?? "stroke-chart-neutral"}
              dash={styles.get(s.stage)?.dash}
            />
          ),
        }));

  return (
    <div data-testid={testId}>
      {/* NFR3: the chart scrolls inside its own box on a narrow viewport instead
          of squeezing a year of days into a phone width — and the scroll stays
          here rather than reaching the page body. Same pattern as
          `CandidatesTable`. */}
      <div className="overflow-x-auto">
        {/* THE FOCUSABLE SURFACE. Two suppressions below, one prose reason for
            both: this div is the chart's interactive surface, and biome has no
            rule that knows charts exist.

            `noNoninteractiveTabindex` — the per-day figures are in no other DOM
            node on the page, so a chart that cannot take focus withholds them from
            every reader who does not use a pointer (FR1.5). `@nu-assist/ui`'s
            `Tooltip` makes the same call for the same reason.

            `useSemanticElements` — the element biome wants for `role="group"` is
            `<fieldset>`, which announces its contents as a grouping of form
            controls and pulls `<legend>` semantics in with it. There is no
            semantic element for "a plot you walk with the arrow keys"; the role,
            the label and the key handling below ARE the accessible-chart
            pattern. */}
        <div
          // NOTE: these two used to carry `biome-ignore` directives. They stopped
          // being needed when `tabIndex` and `role` became EXPRESSIONS — biome's a11y
          // rules only fire on literals. The reasoning is kept because it is still
          // true, and because hardcoding either value again brings the rules back.
          // the chart IS the interactive surface — see the note above
          tabIndex={interactive ? 0 : undefined}
          // <fieldset> would announce a chart as a form-control group — see the note above
          role={interactive ? "group" : undefined}
          aria-label={
            interactive
              ? `${label}. Use the left and right arrow keys to read each ${pointNoun}, Home and End for the ends.`
              : undefined
          }
          data-testid={`${testId}-surface`}
          ref={surface.ref}
          className="relative min-w-[24rem] rounded-xs focus:outline-none focus:ring-1 focus:ring-accent"
          {...(interactive ? crosshair.surfaceProps : {})}
        >
          <svg
            viewBox={`0 0 ${width} ${HEIGHT}`}
            role="presentation"
            aria-hidden="true"
            className="block w-full"
          >
            <line
              x1={PAD_LEFT}
              y1={PAD_TOP}
              x2={width - PAD_RIGHT}
              y2={PAD_TOP}
              className="stroke-border"
              strokeWidth={0.5}
            />
            <text x={PAD_LEFT} y={PAD_TOP - 5} className="fill-text-secondary text-nano">
              {peak}
            </text>
            {/* Drawn BEFORE the series so a line is never hidden under it. */}
            {focused !== null ? (
              <line
                x1={x(focused)}
                y1={PAD_TOP}
                x2={x(focused)}
                y2={PAD_TOP + PLOT_HEIGHT}
                className="stroke-text-secondary"
                strokeWidth={0.75}
                strokeDasharray="2 2"
              />
            ) : null}
            {visible.map((s) => {
              const style = styles.get(s.stage);
              return (
                <polyline
                  key={s.stage}
                  points={s.counts.map((count, i) => `${x(i)},${y(count)}`).join(" ")}
                  fill="none"
                  strokeWidth={1.5}
                  strokeLinejoin="round"
                  strokeDasharray={style?.dash || undefined}
                  className={style?.strokeClass}
                />
              );
            })}
            {/* `stageFillClass`, not the stroke class with `stroke-` swapped for
                `fill-`: Tailwind cannot see a class assembled at runtime, and
                the swap only renders today because `stageFillClass` happens to
                emit the same literals somewhere else in the bundle. Both
                functions index the same palette, so the marker matches its line
                by construction rather than by string surgery. */}
            {focused !== null
              ? visible.map((s) => (
                  <circle
                    key={s.stage}
                    cx={x(focused)}
                    cy={y(s.counts[focused] ?? 0)}
                    // The highlighted series' marker grows, so the chart and the
                    // tooltip agree about which line is meant.
                    r={s.stage === highlightKey ? 4 : 2.5}
                    className={stageFillClass(s.stage)}
                  />
                ))
              : null}
            {ticks.map((tick, i) =>
              i % labelEvery === 0 ? (
                <text
                  key={tick.day}
                  x={x(tick.index)}
                  y={HEIGHT - 8}
                  // Outer ticks anchor to the PLOT, not to their own centre.
                  // The first point sits at PAD_LEFT (8) in a 480-unit viewBox,
                  // and January's label carries a year ("Jan 26"), so a centred
                  // first tick overflows the left edge and renders clipped as
                  // "an 26" — reported on real data whose first day is
                  // 2026-01-30. Found by /pr-review iter 1, cause pinned at
                  // iter 2.
                  textAnchor={i === 0 ? "start" : i === ticks.length - 1 ? "end" : "middle"}
                  className="fill-text-secondary text-nano"
                >
                  {monthLabel(tick.day)}
                </text>
              ) : null
            )}
          </svg>

          {focused !== null ? (
            <ChartTooltip
              testId={`${testId}-tooltip`}
              title={pointLabel(days[focused])}
              rows={tooltipRows}
              side={crosshair.side}
              highlightKey={highlightKey}
              emptyLabel={allHiddenMessage("stage")}
            />
          ) : null}
        </div>
      </div>

      {/* The keyboard reader's copy of the crosshair. Fed only when the index
          came from a key press: a polite live region that also fired on pointer
          moves would announce a careless sweep across a year 366 times. */}
      {interactive ? (
        <output data-testid={`${testId}-readout`} className="sr-only">
          {focused !== null && crosshair.fromKeyboard
            ? `${pointLabel(days[focused])}: ${
                tooltipRows.length === 0
                  ? allHiddenMessage("stage")
                  : tooltipRows.map((row) => `${row.label} ${row.value}`).join(", ")
              }`
            : ""}
        </output>
      ) : null}

      <SeriesLegend
        entries={series.map((s) => ({
          key: s.stage,
          label: s.stage,
          total: s.total,
          strokeClass: styles.get(s.stage)?.strokeClass ?? "stroke-chart-neutral",
          dash: styles.get(s.stage)?.dash,
        }))}
        hidden={hidden}
        onToggle={toggle}
        interactive={interactive}
        testId={testId}
      />

      {/* The all-hidden branch survives `showSummary={false}` on purpose — see
          the prop's own note. Everything else here is the footnote Jared asked
          to lose. */}
      {showSummary || visible.length === 0 ? (
        <p data-testid={`${testId}-summary`} className="mt-1 text-caption text-text-secondary">
          {visible.length === 0
            ? allHiddenMessage("stage")
            : `${visible.length} of ${series.length} stage${
                series.length === 1 ? "" : "s"
              } shown over ${days.length} ${pointNoun}${days.length === 1 ? "" : "s"}, peak ${peak}`}
        </p>
      ) : null}
    </div>
  );
}
